import { dev } from "@phosphor/devstring";
import { type Atom, useStore } from "jotai";
import { memo } from "react";
import { CColor } from "#scripts/keyboard-demo/createKeyboardDemo.ts";
import { CFocusable } from "#scripts/keyboard-demo/plugins/CFocusable.ts";
import type { JotaiStore } from "#scripts/lib/jotai/jotai-types.ts";
import { cn } from "#scripts/lib/utils.ts";
import type { UID } from "#scripts/vmc/WorldState.ts";
import { useComponent } from "#scripts/world-state-demo/world-state-context.ts";

// Guided by @docs/WorldState.md
/**
 * Container component that handles card styling and interactions.
 * Demonstrates separation of concerns: container handles focus/color,
 * while children handle content rendering.
 *
 * Memoized to prevent unnecessary re-renders of children when only
 * container state (focus/color) changes.
 */
export const CardContainer = memo(function CardContainer({ uid, children }: { uid: UID; children: React.ReactNode }) {
  const store = useStore();
  const focusable = useComponent(uid, CFocusable);
  const color = useComponent(uid, CColor);
  return (
    <div
      data-loc="phosphor-blog/scripts/keyboard-demo/components/CardContainer.tsx:26:7" data-name="div" className={cn("demo-card")}
      role="button"
      data-uid={uid}
      tabIndex={0}
      onClick={(event) => {
        if (focusable.handler(dev`Card clicked`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/components/CardContainer.tsx:31:31" }), "enter")) {
          event.stopPropagation();
          event.preventDefault();
        }
      }}
      onFocus={() => focusable.handler(dev`Card focused`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/components/CardContainer.tsx:36:40" }), "enter")}
      ref={(element) => {
        if (!element) return;
        // For demonstration, we're showing a way to minimize re-renders, here.
        const unsub1 = onChange(store, focusable.hasDirectFocusAtom, (isFocused) => {
          if (isFocused) requestAnimationFrame(() => element.focus());
        });
        const unsub2 = onChange(store, color.borderColorAtom, (borderColor) => {
          element.style.borderColor = borderColor;
        });
        return () => {
          unsub1();
          unsub2();
        };
      }}
    >
      {children}
    </div>
  );
});

function onChange<T>(
  store: JotaiStore,
  atom: Atom<T>,
  fn: (value: T) => void,
  eq: (a: T, b: T) => boolean = Object.is,
) {
  let lastValue: T | undefined;
  const update = () => {
    const value = store.get(atom);
    if (lastValue === value || (lastValue !== undefined && eq(value, lastValue))) return;
    lastValue = value;
    fn(value);
  };
  update();
  return store.sub(atom, update);
}
